import { compileAssessment } from './curatorCriteria';
import { amplitudeSeparations } from './amplitudeSeparation';
// UI rollout only; recording/file authorization remains with the existing APIs.
const PREVIEW_ACCOUNTS = new Set(['admin', 'Mohamed_aziz', 'jes_at_ece_wisc_edu']);
export const canUseCuratorPreview = user => Boolean(
  user && user.is_active !== false && PREVIEW_ACCOUNTS.has(user.username)
);

export const PREVIEW_DEFAULTS = {
  minimumSpikes: '1', maximumIsiPercent: '2', minimumSeparation: '',
  invert: false, waveformHalfWidthMs: '1', neighborRadius: '3',
  openByDefault: false, criteriaMode: 'thresholds', criteriaFormula: 'spikes >= 1 AND isi <= 2',
  highlightRows: true, passColor: '#dcfce7', reviewColor: '#fee2e2', unassessedColor: '#fef3c7',
  minimumRate: '', maximumRate: '', minimumAmplitude: '', maximumAmplitude: '',
};

export const previewStorageKey = user => `spikescope_curator_preview:v1:${user?.id ?? user?.username}`;
export const readPreviewPreferences = user => {
  try {
    return { ...PREVIEW_DEFAULTS, ...JSON.parse(localStorage.getItem(previewStorageKey(user)) || '{}') };
  } catch { return { ...PREVIEW_DEFAULTS }; }
};

export const finiteNumber = value => value === null || value === undefined || value === ''
  || typeof value === 'boolean' ? null : Number.isFinite(Number(value)) ? Number(value) : null;

export const validateThresholds = settings => compileAssessment(settings).error;
export const assessUnit = (metrics, settings) => compileAssessment(settings).assess(metrics);

// Explicit metadata is required: do not infer duration from the last spike or
// silently assume a sampling rate when judging another sorter's results.
export function prepareCuratorRun(dataset, { sampleRateHz, durationSeconds, timeUnit, percentage }) {
  const rate = finiteNumber(sampleRateHz), duration = finiteNumber(durationSeconds), percent = finiteNumber(percentage);
  if (!(rate > 0) || !(duration > 0) || !(percent > 0 && percent <= 100)) {
    throw new Error('Enter a positive sample rate and recording duration, and an interval from 1–100%.');
  }
  if (!['samples', 'seconds', 'milliseconds'].includes(timeUnit)) throw new Error('Choose the spike timestamp unit.');
  if (!dataset?.clusters?.length) throw new Error('Choose sorting results first.');
  const ids = new Set();
  const scale = timeUnit === 'seconds' ? rate : timeUnit === 'milliseconds' ? rate / 1000 : 1;
  const durationSamples = duration * rate * percent / 100;
  let excludedSpikes = 0;
  const clusters = dataset.clusters.map(cluster => {
    const id = String(cluster.id);
    if (ids.has(id)) throw new Error(`Duplicate unit ID ${id}. Unit IDs must be unique.`);
    ids.add(id);
    const originalTimes = cluster.spikeTimes || [];
    const retained = [];
    originalTimes.forEach((raw, index) => {
      const time = finiteNumber(raw);
      if (time === null || time < 0) throw new Error(`Unit ${id} has an invalid spike time.`);
      const samples = Math.round(time * scale);
      if (samples < durationSamples) retained.push({ time: samples, index });
      else excludedSpikes++;
    });
    const fullUnit = retained.length === originalTimes.length && percent === 100;
    const spikeAmplitudes = cluster.spikeAmplitudes?.length === originalTimes.length
      ? retained.map(({ index }) => cluster.spikeAmplitudes[index]) : fullUnit ? cluster.spikeAmplitudes || [] : [];
    const originalIndices = new Set(retained.map(item => item.index));
    const waveforms = (cluster.waveforms || []).filter(w => fullUnit || originalIndices.has(w.spikeIndex));
    const times = retained.map(item => item.time).sort((a, b) => a - b);
    const intervals = times.slice(1).map((time, i) => (time - times[i]) / rate * 1000);
    const amplitudes = spikeAmplitudes.map(finiteNumber).filter(v => v !== null);
    return { ...cluster, spikeTimes: retained.map(item => item.time), spikeCount: retained.length,
      spikeAmplitudes, waveforms, intervals,
      metrics: {
        spikeCount: retained.length,
        firingRateHz: retained.length / (duration * percent / 100),
        isiPercent: intervals.length ? intervals.filter(value => value < 2).length / intervals.length * 100 : retained.length === 1 ? 0 : null,
        meanAmplitude: amplitudes.length ? amplitudes.reduce((a, b) => a + b, 0) / amplitudes.length : null,
        separation: null,
      },
    };
  });
  const separations = amplitudeSeparations(clusters);
  clusters.forEach(cluster => { cluster.metrics.separation = separations.get(String(cluster.id)); });
  return { ...dataset, clusters, excludedSpikes, sampleRateHz: rate, durationSeconds: duration * percent / 100,
    metadata: { ...dataset.metadata, timeUnit: 'samples', sampleRateHz: rate, durationSamples } };
}

export function filterAndSortUnits(units, { query = '', status = 'All', sortKey = 'id', direction = 'asc', names = {}, channelNames = {}, labelChannel = value => value }) {
  const search = query.trim().toLowerCase();
  return units.filter(unit => (status === 'All' || unit.assessment.status === status)
    && (!search || [unit.id, names[unit.id], labelChannel(unit.primaryChannel), channelNames[unit.primaryChannel]]
      .some(value => String(value ?? '').toLowerCase().includes(search))))
    .sort((a, b) => {
      const left = sortKey === 'status' ? a.assessment.status : a.metrics[sortKey] ?? a[sortKey];
      const right = sortKey === 'status' ? b.assessment.status : b.metrics[sortKey] ?? b[sortKey];
      if (left == null) return right == null ? 0 : 1;
      if (right == null) return -1;
      const order = typeof left === 'number' && typeof right === 'number'
        ? left - right : String(left).localeCompare(String(right), undefined, { numeric: true });
      return direction === 'asc' ? order : -order;
    });
}
